import {
  Component,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  AfterViewInit,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SettingsService } from '../app/core/service/settings/settings.service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationsHubService } from 'src/app/core/service/hubs/notifications-hub.service';
import mixpanel from 'mixpanel-browser';
import posthog from 'posthog-js';
import { environment } from '../environments/environment';
import { Router } from '@angular/router';
import * as CryptoJS from 'crypto-js';
import { ManageIntercom } from './core/util/manage-intercom';
import { AppConfigService } from './appConfig.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  title = 'Synced';

  // Layout bindings
  @HostBinding('class.layout-fixed') get isFixed() { return this.settings.layout.isFixed; }
  @HostBinding('class.aside-collapsed') get isCollapsed() { return this.settings.layout.isCollapsed; }
  @HostBinding('class.layout-boxed') get isBoxed() { return this.settings.layout.isBoxed; }
  @HostBinding('class.layout-fs') get useFullLayout() { return this.settings.layout.useFullLayout; }
  @HostBinding('class.hidden-footer') get hiddenFooter() { return this.settings.layout.hiddenFooter; }
  @HostBinding('class.layout-h') get horizontal() { return this.settings.layout.horizontal; }
  @HostBinding('class.aside-float') get isFloat() { return this.settings.layout.isFloat; }
  @HostBinding('class.offsidebar-open') get offsidebarOpen() { return this.settings.layout.offsidebarOpen; }
  @HostBinding('class.aside-toggled') get asideToggled() { return this.settings.layout.asideToggled; }
  @HostBinding('class.aside-collapsed-text') get isCollapsedText() { return this.settings.layout.isCollapsedText; }

  private static mixpanelInitialized = false;

  /**
   * The visitor's PostHog id from the marketing site (trysynced.com adds `ph_did` to its links into
   * the app), read on load, before the router can rewrite the URL. PostHog starts with it, so the
   * signup that follows is joined to the website visits before it.
   */
  private static readonly websiteVisitorId: string | null = (() => {
    if (typeof window === 'undefined') return null;
    const id = new URLSearchParams(window.location.search).get('ph_did');
    return id && /^[A-Za-z0-9-]{8,64}$/.test(id) ? id : null;
  })();

  constructor(
    public settings: SettingsService,
    private dialog: MatDialog,
    private readonly _notificationsHubService: NotificationsHubService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private configService: AppConfigService  // ✅ Add this
  ) {
    this.router.events.subscribe(() => {
      window.Intercom?.('hide');
    });
  }

  /**
   * SD-1234: the inbox assistant's Gmail frame (#/assistant-frame) is this app drawn inside a
   * 360px popover on Gmail. It needs none of the page furniture: no chat launcher, no hub
   * connection, no analytics for each email opened.
   */
  private static get isAssistantFrame(): boolean {
    return /\/assistant-frame/.test((window.location.hash || '') + (window.location.pathname || ''));
  }

  async ngOnInit() {
    if (AppComponent.isAssistantFrame) return;
    const token = localStorage.getItem('AccessToken');
    if (token) {
      await this._notificationsHubService.start();
    }
    // Boot Intercom with correct user + company data (or anonymous if not logged in)
    ManageIntercom.identifyUser(true,this.configService);

    window.Intercom?.('onHide', () => {
      window.dispatchEvent(new CustomEvent('intercom:closed'));
    });
  }

  ngAfterViewInit(): void {
    // ✅ Delay Mixpanel init until browser is idle — avoids SSR hydration conflicts
    if (isPlatformBrowser(this.platformId) && !AppComponent.mixpanelInitialized && !AppComponent.isAssistantFrame) {
      AppComponent.mixpanelInitialized = true;

      // Wait until the browser is fully idle (no pending hydration or rendering)
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(() => {
          this.initializePostHog();
          this.initializeMixpanel();
        }, { timeout: 3000 });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          this.initializePostHog();
          this.initializeMixpanel();
        }, 2000);
      }
    }
  }

  private initializePostHog() {
    try {
      // Someone arriving from the website, not signed in: carry on as the visitor the website saw.
      const websiteVisitorId = localStorage.getItem('Id') ? null : AppComponent.websiteVisitorId;
      posthog.init(this.configService.posthogKey, {  // ✅ Replace environment.posthogKey
        api_host: this.configService.posthogHost,    // ✅ Replace environment.posthogHost
        defaults: '2026-01-30',
        ...(websiteVisitorId ? { bootstrap: { distinctID: websiteVisitorId } } : {}),
      });

      // Identify user if already logged in (handles page refresh)
      const userId = localStorage.getItem('Id');
      if (userId) {
        posthog.identify(userId, {
          email: localStorage.getItem('Email'),
          name: localStorage.getItem('UserName'),
        });
        const orgId = localStorage.getItem('SelectedOrganization');
        if (orgId) {
          posthog.group('company', orgId, {
            name: localStorage.getItem('SelectedOrganizationName') || orgId,
          });
        }
      }
    } catch (err) {
      console.error('PostHog initialization error:', err);
    }
  }

  private initializeMixpanel() {
    try {
      mixpanel.init('e707a7324df89e08608baa06f42aa847', {
        debug: false,
        track_pageview: true,
        persistence: 'localStorage',
        autocapture: true,                // ✅ required feature
        record_sessions_percent: 100,     // can lower later if needed
      });
    } catch (err) {
      console.error('❌ Mixpanel initialization error:', err);
    }
  }

  ngOnDestroy(): void {
    this._notificationsHubService.stop();
  }
}
